import { useState } from "react";
import {
  usePaymentTerms,
  useCreatePaymentTerm,
  useUpdatePaymentTerm,
  useDeletePaymentTerm,
  type PaymentTerms,
} from "@/api/payments";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Loader2, Plus, Pencil, Trash2, Calendar } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

interface PaymentTermFormData {
  name: string;
  code: string;
  paymentDueDays: string;
  dayInterval: string;
  description: string;
  sortOrder: string;
}

export function NetTermsManager() {
  const { data: paymentTerms, isLoading } = usePaymentTerms();
  const createMutation = useCreatePaymentTerm();
  const updateMutation = useUpdatePaymentTerm();
  const deleteMutation = useDeletePaymentTerm();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingTerm, setEditingTerm] = useState<PaymentTerms | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [termToDelete, setTermToDelete] = useState<PaymentTerms | null>(null);

  const [formData, setFormData] = useState<PaymentTermFormData>({
    name: "",
    code: "",
    paymentDueDays: "",
    dayInterval: "30",
    description: "",
    sortOrder: "0",
  });

  const resetForm = () => {
    setFormData({
      name: "",
      code: "",
      paymentDueDays: "",
      dayInterval: "30",
      description: "",
      sortOrder: "0",
    });
    setEditingTerm(null);
  };

  const handleOpenCreate = () => {
    resetForm();
    setDialogOpen(true);
  };

  const handleOpenEdit = (term: PaymentTerms) => {
    setEditingTerm(term);
    setFormData({
      name: term.name,
      code: term.code,
      paymentDueDays: term.paymentDueDays.toString(),
      dayInterval: term.dayInterval.toString(),
      description: term.description || "",
      sortOrder: term.sortOrder.toString(),
    });
    setDialogOpen(true);
  };

  const handleSubmit = async () => {
    try {
      const data = {
        name: formData.name,
        code: formData.code,
        paymentDueDays: parseInt(formData.paymentDueDays, 10),
        dayInterval: parseInt(formData.dayInterval, 10),
        description: formData.description || undefined,
        sortOrder: parseInt(formData.sortOrder, 10),
      };

      if (editingTerm) {
        await updateMutation.mutateAsync({ id: editingTerm.id, ...data });
        toast.success("Payment term updated", {
          description: "The payment term has been updated successfully.",
        });
      } else {
        await createMutation.mutateAsync(data);
        toast.success("Payment term created", {
          description: "The payment term has been created successfully.",
        });
      }

      setDialogOpen(false);
      resetForm();
    } catch (error: unknown) {
      const err = error as { response?: { data?: { error?: string } } };
      toast.error(err.response?.data?.error || "Failed to save payment term");
    }
  };

  const handleDelete = async () => {
    if (!termToDelete) return;

    try {
      await deleteMutation.mutateAsync(termToDelete.id);
      toast.success("Payment term deleted", {
        description: "The payment term has been deactivated successfully.",
      });
      setDeleteDialogOpen(false);
      setTermToDelete(null);
    } catch (error: unknown) {
      const err = error as { response?: { data?: { error?: string } } };
      toast.error(err.response?.data?.error || "Failed to delete payment term");
    }
  };

  const handleOpenDelete = (term: PaymentTerms) => {
    setTermToDelete(term);
    setDeleteDialogOpen(true);
  };

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>NET Terms Configuration</CardTitle>
              <CardDescription>
                Manage NET payment terms available for customer orders
              </CardDescription>
            </div>
            <Button onClick={handleOpenCreate}>
              <Plus className="mr-2 h-4 w-4" />
              Add NET Term
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin" />
            </div>
          ) : paymentTerms && paymentTerms.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Code</TableHead>
                  <TableHead>Due Days</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {paymentTerms
                  .sort((a, b) => a.sortOrder - b.sortOrder)
                  .map((term) => (
                    <TableRow key={term.id}>
                      <TableCell className="font-medium">{term.name}</TableCell>
                      <TableCell>
                        <code className="text-sm bg-muted px-2 py-1 rounded">
                          {term.code}
                        </code>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <Calendar className="h-3 w-3 text-muted-foreground" />
                          {term.paymentDueDays} days
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge
                          variant={term.isActive ? "default" : "secondary"}
                        >
                          {term.isActive ? "Active" : "Inactive"}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleOpenEdit(term)}
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleOpenDelete(term)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          ) : (
            <div className="text-center py-8 text-muted-foreground">
              No payment terms configured. Create your first NET term to get
              started.
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-[525px]">
          <DialogHeader>
            <DialogTitle>
              {editingTerm ? "Edit NET Term" : "Create NET Term"}
            </DialogTitle>
            <DialogDescription>
              Configure a NET payment term for customer invoicing
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="name">Name *</Label>
                <Input
                  id="name"
                  placeholder="NET 30"
                  value={formData.name}
                  onChange={(e) =>
                    setFormData({ ...formData, name: e.target.value })
                  }
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="code">Code *</Label>
                <Input
                  id="code"
                  placeholder="NET30"
                  value={formData.code}
                  onChange={(e) =>
                    setFormData({ ...formData, code: e.target.value })
                  }
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="paymentDueDays">Payment Due (Days) *</Label>
              <Input
                id="paymentDueDays"
                type="number"
                min="0"
                placeholder="30"
                value={formData.paymentDueDays}
                onChange={(e) =>
                  setFormData({ ...formData, paymentDueDays: e.target.value })
                }
              />
              <p className="text-xs text-muted-foreground">
                Number of days until payment is due after invoice
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="dayInterval">Billing Cycle (days) *</Label>
              <Input
                id="dayInterval"
                type="number"
                min="1"
                value={formData.dayInterval}
                onChange={(e) =>
                  setFormData({ ...formData, dayInterval: e.target.value })
                }
              />
              <p className="text-xs text-muted-foreground">
                How often invoices are generated
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="sortOrder">Sort Order</Label>
              <Input
                id="sortOrder"
                type="number"
                min="0"
                value={formData.sortOrder}
                onChange={(e) =>
                  setFormData({ ...formData, sortOrder: e.target.value })
                }
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                placeholder="Optional description for this payment term..."
                value={formData.description}
                onChange={(e) =>
                  setFormData({ ...formData, description: e.target.value })
                }
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setDialogOpen(false);
                resetForm();
              }}
              disabled={createMutation.isPending || updateMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={
                createMutation.isPending ||
                updateMutation.isPending ||
                !formData.name ||
                !formData.code ||
                !formData.paymentDueDays
              }
            >
              {(createMutation.isPending || updateMutation.isPending) && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              {editingTerm ? "Update" : "Create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Payment Term</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{termToDelete?.name}"? This will
              deactivate the payment term and it will no longer be available for
              new customer assignments.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
